import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withRouter, useParams } from 'react-router-dom';
import { compose } from 'redux';
import { useDispatch } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import SectionLoading from '@components/SectionLoading';
import Text, { TEXT_COLOR, TEXT_SIZE } from '@components/Text';
import ChatServiceV2 from '@services/chatServiceV2';
import { ROUTES } from '@shared/constants';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { setVideoChatV2InMeeting } from '@containers/App/actions';
import {
  MeetingProvider,
  AudioVideoProvider,
  DevicesProvider,
  LocalVideoProvider,
  RemoteVideoTileProvider,
  FeaturedVideoTileProvider,
  ContentShareProvider,
  RosterProvider,
  UserActivityProvider,
  MeetingManager,
  LoggerProvider,
  GlobalStyles,
} from 'amazon-chime-sdk-component-library-react';
import { ConsoleLogger, LogLevel, MeetingSessionConfiguration } from 'amazon-chime-sdk-js';
import WaitingRoom from './components/WaitingRoom';
import LiveMeetingStage from './components/LiveMeetingStage';
import LeavePopupIcon from './components/LeavePopupIcon';
import {
  getErrorMessage,
  getEndedReasonFromErrorMessage,
  isDeviceAccessError,
} from './utils/errorUtils';
import requestMediaDevicesPermissions from './utils/requestMediaDevicesPermissions';
import { videoChatTheme } from './styles/theme';
import {
  LeaveLoadingOverlay,
  LeaveLoadingCard,
  LeaveLoadingText,
} from './styles/style';
import TimeTracker from '@shared/TimeTracker';

const VideoChatV2 = ({ history, t }) => {
  const { chatId } = useParams();
  const [loading, setLoading] = useState(true);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [meetingPayload, setMeetingPayload] = useState(null);
  const [waitingRoomOpen, setWaitingRoomOpen] = useState(true);
  const [cameraOn, setCameraOn] = useState(true);
  const [audioInputDeviceId, setAudioInputDeviceId] = useState(null);
  const [audioOutputDeviceId, setAudioOutputDeviceId] = useState(null);
  const [videoInputDeviceId, setVideoInputDeviceId] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [leavingInProgress, setLeavingInProgress] = useState(false);
  const dispatch = useDispatch();

  const logger = useMemo(() => new ConsoleLogger('VideoChatV2', LogLevel.ERROR), []);
  const meetingManager = useMemo(() => new MeetingManager(logger), [logger]);

  const redirectToMeetingEnded = useCallback((reason) => {
    const safeReason = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    window.location.assign(`${ROUTES.VIDEO_CHAT_ENDED}${safeReason}`);
  }, [history]);

  const isTherapistInitiator = useMemo(
    () => !!sessionInfo?.users?.some((user) => user.self && user.initiator),
    [sessionInfo],
  );

  const user = useMemo(() => sessionInfo?.users?.find((user) => !user.initiator), [sessionInfo]);

  const performLeave = useCallback(async ({ endForAll = false } = {}) => {
    setLeavingInProgress(true);
    dispatch(setVideoChatV2InMeeting(false));

    try {
      if (endForAll) {
        let timeoutId;
        try {
          await Promise.race([
            ChatServiceV2.endChatV2(chatId),
            new Promise((_, reject) => {
              timeoutId = setTimeout(() => reject(new Error('timeout')), 7000);
            }),
          ]);
        } finally {
          clearTimeout(timeoutId);
        }
      }
    } catch (_error) {
      // Best-effort cleanup; tab should still close.
    } finally {
      try {
        window.open('', '_self');
      } catch (_error) {
        // Non-fatal.
      }
      window.close();
      setTimeout(() => {
        window.location.assign(`${ROUTES.VIDEO_CHAT_ENDED}?reason=left`);
      }, 120);
    }
  }, [chatId, dispatch]);

  const openMentorLeaveOptions = useCallback(() => {
    dispatch(openPopUp(POPUP_TYPES.VIDEO_CHAT_V2_LEAVE_OPTIONS, {
      hideCloseIcon: true,
      disableBackdrop: true,
      icon: <LeavePopupIcon />,
      titleText: t('VideoChatV2.confirmLeaveTherapist.title', 'Leave Meeting'),
      bodyText: t(
        'VideoChatV2.confirmLeaveTherapist.body',
        'Do you want to end the meeting for all participants?',
      ),
      endForAllText: t('VideoChatV2.confirmLeaveTherapist.confirm', 'Yes - End meeting'),
      leaveOnlyMeText: t('VideoChatV2.confirmLeaveTherapist.leaveOnlyMe', 'No - Leave without ending'),
      cancelText: t('VideoChatV2.confirmLeaveTherapist.cancel', 'Cancel'),
      onSelect: (selection) => {
        if (selection === 'end_for_all') {
          performLeave({ endForAll: true });
          return;
        }

        if (selection === 'leave_only_me') {
          performLeave({ endForAll: false });
        }
      },
    }));
  }, [dispatch, performLeave, t]);

  const handleLeave = useCallback(() => {
    if (isTherapistInitiator) {
      openMentorLeaveOptions();
      return;
    }

    dispatch(openPopUp(POPUP_TYPES.CONFIRM, {
      hideCloseIcon: true,
      disableBackdrop: true,
      icon: <LeavePopupIcon />,
      titleText: t('VideoChatV2.confirmLeave.title'),
      bodyText: t('VideoChatV2.confirmLeave.body'),
      confirmText: t('VideoChatV2.confirmLeave.confirm'),
      cancelText: t('VideoChatV2.confirmLeave.cancel'),
      onConfirm: (confirmed) => {
        if (confirmed) {
          performLeave({ endForAll: false });
        }
      },
    }));
  }, [dispatch, isTherapistInitiator, openMentorLeaveOptions, performLeave, t]);

  const handleJoinFromWaitingRoom = useCallback(async () => {
    const { canJoin, cameraAvailable } = await requestMediaDevicesPermissions({
      dispatch,
      openPopUp,
      popupType: POPUP_TYPES.MEDIA_DEVICES_PERMISSIONS,
    });

    if (!canJoin) {
      return;
    }

    if (!cameraAvailable) {
      setCameraOn(false);
    }
    setWaitingRoomOpen(false);
  }, [dispatch]);

  useEffect(() => {
    const loadChat = async () => {
      try {
        const result = await ChatServiceV2.getChatInfoV2(chatId);

        if (result?.error) {
          const message = getErrorMessage(result.error) || 'Unable to load chat info';
          throw new Error(message);
        }

        const hasChimeSession = !!(result?.sessionInfo && result?.chimeInfo?.meeting && result?.chimeInfo?.attendee);

        if (!hasChimeSession) {
          throw new Error('Chime session is unavailable');
        }

        setSessionInfo(result.sessionInfo);
        setMeetingPayload({
          meeting: result.chimeInfo.meeting,
          attendee: result.chimeInfo.attendee,
        });
        setCameraOn(true);
        setAudioInputDeviceId(null);
        setAudioOutputDeviceId(null);
        setVideoInputDeviceId(null);
        setSessionReady(false);
        setLoading(false);
      } catch (error) {
        dispatch(setVideoChatV2InMeeting(false));
        setLoading(false);
        const reason = getEndedReasonFromErrorMessage(getErrorMessage(error));
        redirectToMeetingEnded(reason);
      }
    };

    loadChat();
    // No cleanup here — session cleanup is handled by the unmount effect below.
  }, [chatId, dispatch, redirectToMeetingEnded]);

  useEffect(() => {
    const prepareSession = async () => {
      if (sessionReady || waitingRoomOpen === false) {
        return;
      }

      if (!meetingPayload?.meeting || !meetingPayload?.attendee) {
        return;
      }

      try {
        const configuration = new MeetingSessionConfiguration(meetingPayload.meeting, meetingPayload.attendee);
        // Prefer full device labels for preview controls.
        // If browser/device permission blocks it, fallback to labels=0 so
        // user can still stay in waiting room instead of false "unavailable".
        try {
          // deviceLabels: 7 = AudioInput(1) | AudioOutput(2) | VideoInput(4)
          await meetingManager.join(configuration, { deviceLabels: 7 });
        } catch (joinError) {
          if (!isDeviceAccessError(joinError)) {
            throw joinError;
          }

          await meetingManager.join(configuration, { deviceLabels: 0 });
        }

        // Explicitly choose the default video input device so PreviewVideo
        // has an active stream. join() only enumerates labels; it does not
        // call chooseVideoInputDevice(), which is required for the camera
        // stream to flow.
        try {
          const av = meetingManager.meetingSession?.audioVideo;
          if (av) {
            const videoDevices = await av.listVideoInputDevices();
            if (videoDevices?.length > 0) {
              await av.chooseVideoInputDevice(videoDevices[0].deviceId);
            }
          }
        } catch (_deviceError) {
          // Non-fatal — preview may be blank but meeting can still proceed.
        }

        setSessionReady(true);
      } catch (error) {
        if (isDeviceAccessError(error)) {
          // Keep user on waiting room; meeting exists but device access failed.
          // Stage join may still proceed once user grants/changes permissions.
          setSessionReady(true);
          return;
        }

        dispatch(setVideoChatV2InMeeting(false));
        redirectToMeetingEnded('unavailable');
      }
    };

    prepareSession();
    // Do not leave meeting manager here. This effect re-runs when waitingRoomOpen
    // flips to false on Join, and cleanup would tear down the prejoin session
    // right before stage start, causing perpetual loading.
  }, [
    chatId,
    dispatch,
    isDeviceAccessError,
    meetingManager,
    meetingPayload,
    redirectToMeetingEnded,
    sessionReady,
    waitingRoomOpen,
  ]);

  useEffect(() => () => {
    dispatch(setVideoChatV2InMeeting(false));
  }, [dispatch]);

  if (loading) {
    return <SectionLoading />;
  }

  return (
    <ThemeProvider theme={videoChatTheme}>
      {isTherapistInitiator && !!user && !!chatId && (
        <TimeTracker
          key={chatId}
          userId={user.participantId}
          activityId={chatId}
          activityType="THERAPY_MEETING"
          hidden
        />
      )}
      <GlobalStyles />
      {leavingInProgress && (
        <LeaveLoadingOverlay>
          <LeaveLoadingCard>
            <SectionLoading size={54} />
            <LeaveLoadingText>
              <Text size={TEXT_SIZE.T2} textColor={TEXT_COLOR.DARK_BLUE} weight='700'>
                {t('VideoChatV2.leavingInProgress')}
              </Text>
            </LeaveLoadingText>
          </LeaveLoadingCard>
        </LeaveLoadingOverlay>
      )}
      <LoggerProvider logger={logger}>
        <MeetingProvider meetingManager={meetingManager}>
          <AudioVideoProvider>
            <DevicesProvider>
              <LocalVideoProvider>
                <RemoteVideoTileProvider>
                  <FeaturedVideoTileProvider>
                    <ContentShareProvider>
                      <RosterProvider>
                        <UserActivityProvider>
                          {waitingRoomOpen ? (
                            <WaitingRoom
                              users={sessionInfo?.users}
                              cameraOn={cameraOn}
                              onCameraToggle={setCameraOn}
                              onAudioInputSelected={setAudioInputDeviceId}
                              onAudioOutputSelected={setAudioOutputDeviceId}
                              initialAudioOutputDeviceId={audioOutputDeviceId}
                              onVideoInputSelected={setVideoInputDeviceId}
                              onJoin={handleJoinFromWaitingRoom}
                              onLeave={handleLeave}
                            />
                          ) : (
                            <LiveMeetingStage
                              meetingPayload={meetingPayload}
                              users={sessionInfo?.users}
                              initialCameraOn={cameraOn}
                              initialAudioInputDeviceId={audioInputDeviceId}
                              initialVideoInputDeviceId={videoInputDeviceId}
                              initialAudioOutputDeviceId={audioOutputDeviceId}
                              onAudioOutputSelected={setAudioOutputDeviceId}
                              onLeave={handleLeave}
                              onMeetingEnded={() => {
                                history.push(ROUTES.VIDEO_CHAT_ENDED);
                              }}
                              onJoinFailed={(reason) => {
                                if (isDeviceAccessError(reason)) {
                                  dispatch(openPopUp(POPUP_TYPES.MEDIA_DEVICES_PERMISSIONS));
                                  setWaitingRoomOpen(true);
                                  return;
                                }
                                redirectToMeetingEnded('unavailable');
                              }}
                            />
                          )}
                        </UserActivityProvider>
                      </RosterProvider>
                    </ContentShareProvider>
                  </FeaturedVideoTileProvider>
                </RemoteVideoTileProvider>
              </LocalVideoProvider>
            </DevicesProvider>
          </AudioVideoProvider>
        </MeetingProvider>
      </LoggerProvider>
    </ThemeProvider>
  );
};

export default compose(withRouter, withTranslation())(VideoChatV2);
